// ===========================================
// Pending Time-Off Requests Dialog (admin inbox)
// ===========================================
// School-wide list of PENDING teacher time-off requests, opened from the
// Teachers page header badge. Approve/decline routes through the same
// ReviewTimeOffDialog (with the affected-lessons preview) as the per-teacher
// panel.

import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { BeachAccess as TimeOffIcon } from '@mui/icons-material';
import { usePendingTimeOff } from '../../hooks/useTeacherUnavailability';
import { TeacherUnavailabilityPeriod } from '../../api/users.api';
import { ReviewTimeOffDialog } from './TeacherTimeOffPanel';
import { timeOffRangeLabel, timeOffErrorMessage } from '../../utils/timeOff';

interface PendingTimeOffDialogProps {
  open: boolean;
  onClose: () => void;
}

export default function PendingTimeOffDialog({ open, onClose }: PendingTimeOffDialogProps) {
  const { data: pending, isLoading, isError, error } = usePendingTimeOff({ enabled: open });
  const [review, setReview] = useState<{ mode: 'approve' | 'decline'; period: TeacherUnavailabilityPeriod } | null>(
    null
  );

  const list = pending ?? [];

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TimeOffIcon color="primary" />
            Pending Time-Off Requests
          </Box>
        </DialogTitle>
        <DialogContent>
          {isLoading ? (
            <Box sx={{ py: 3, textAlign: 'center' }}>
              <CircularProgress size={28} />
            </Box>
          ) : isError ? (
            <Alert severity="error">{timeOffErrorMessage(error)}</Alert>
          ) : list.length === 0 ? (
            <Box sx={{ py: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">No pending requests — all caught up.</Typography>
            </Box>
          ) : (
            <List disablePadding>
              {list.map((period) => {
                const teacherName = period.Teacher
                  ? `${period.Teacher.User.firstName} ${period.Teacher.User.lastName}`
                  : 'Unknown teacher';
                return (
                  <ListItem key={period.id} divider sx={{ px: 0, py: 1 }}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="body2" fontWeight={600}>
                            {teacherName}
                          </Typography>
                          <Chip
                            label={timeOffRangeLabel(period)}
                            size="small"
                            sx={{ bgcolor: '#FCF6E6', fontWeight: 500 }}
                          />
                        </Box>
                      }
                      secondary={period.reason || undefined}
                    />
                    <Stack direction="row" spacing={0.5}>
                      <Button size="small" variant="contained" onClick={() => setReview({ mode: 'approve', period })}>
                        Approve
                      </Button>
                      <Button size="small" color="error" onClick={() => setReview({ mode: 'decline', period })}>
                        Decline
                      </Button>
                    </Stack>
                  </ListItem>
                );
              })}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      <ReviewTimeOffDialog
        open={!!review}
        onClose={() => setReview(null)}
        mode={review?.mode ?? 'approve'}
        period={review?.period ?? null}
      />
    </>
  );
}
